import { Directory, Filesystem } from '@capacitor/filesystem';

import { StorageService } from '@services/storage/storage.service';
import { UploadEntityType } from './upload-urls';

const LEGACY_KEY = 'INCOMPLETE_UL';

// Set once the sweep below has run. Its absence is what makes this a
// migration rather than a recurring sweep -- see the doc comment.
const CLEARED_KEY = 'LEGACY_PHOTO_QUEUE_CLEARED';

// The three photo types the pre-native upload queue ever wrote. Anything
// else found under LEGACY_KEY belongs to a still-live path (see below) and
// must not be touched.
const PHOTO_ENTITY_TYPES: UploadEntityType[] = [
  'JobMedia',
  'JobIncidentMedia',
  'JobRestorationMedia',
];

interface LegacyUpload {
  entityType: string;
  entityId: string;
  file: string;
}

// Extracted so specs can substitute a fake without touching the real
// filesystem. @capacitor/filesystem's Filesystem export is a Proxy whose
// `get` trap unconditionally builds a fresh method wrapper on every access
// -- it never consults properties assigned onto it -- so neither
// `spyOn(Filesystem, 'deleteFile')` nor reassigning the module export can
// intercept calls under this project's es2020 module output. This seam is
// the only mockable point.
type FileDeleter = (path: string) => Promise<void>;

async function deleteFromDataDirectory(path: string): Promise<void> {
  await Filesystem.deleteFile({ path, directory: Directory.Data });
}

/**
 * One-time removal of the pre-native photo upload queue.
 *
 * INCOMPLETE_UL is not exclusively the retired photo queue's key -- it is
 * still written today by the document path (JobDocumentService.uploadFile()
 * -> S3UploadService.upload() -> interruptUploads()) whenever the app
 * backgrounds mid-upload, and background-upload.effect.ts's resumeUpload$
 * reads it on appResume to continue those uploads. An unscoped sweep here
 * can win the race against that resume and permanently delete an in-flight
 * document upload with no error surfaced. So this only ever deletes files
 * and drops entries for the three retired photo entity types; everything
 * else -- including unrecognised entityTypes -- is written back under
 * LEGACY_KEY untouched so the document resume path still finds it.
 *
 * The photo entries themselves are discarded rather than migrated: crews
 * had stopped using the feature, so nothing here is being waited on, and
 * the old writer passed a full `data:` URL to Filesystem.writeFile -- so
 * whether these files are JPEGs or base64 text is unresolved and not worth
 * resolving.
 *
 * Only files named in INCOMPLETE_UL are touched. The old code never deleted
 * files after a successful resume, so Directory.Data very likely holds
 * orphans from years of use; sweeping those blindly is riskier than leaving
 * them.
 *
 * Runs exactly once per install, guarded by CLEARED_KEY, and that guard is
 * load-bearing rather than an optimisation. The native uploader has a
 * server-controlled kill switch; when it is off, photo uploads go back
 * through S3UploadService, whose interruptUploads() writes photo entries
 * into INCOMPLETE_UL whenever the app backgrounds mid-upload. A sweep that
 * ran on every launch would then delete exactly the entries the fallback
 * path had just persisted -- silently, and only for the users the kill
 * switch exists to rescue. The marker is set even when the key is absent,
 * so the window closes on the first launch after the upgrade either way.
 */
export async function clearLegacyUploadQueue(
  storage: StorageService,
  deleteFile: FileDeleter = deleteFromDataDirectory
): Promise<void> {
  const keys = await storage.keys();
  if (keys.includes(CLEARED_KEY)) {
    return;
  }

  if (!keys.includes(LEGACY_KEY)) {
    await storage.set(CLEARED_KEY, true);
    return;
  }

  let uploads: LegacyUpload[] = [];
  try {
    uploads = JSON.parse((await storage.get(LEGACY_KEY)) || '[]');
  } catch {
    uploads = [];
  }

  const isPhoto = (upload: LegacyUpload) =>
    PHOTO_ENTITY_TYPES.includes(upload.entityType as UploadEntityType);

  const toDelete = uploads.filter(isPhoto);
  const toKeep = uploads.filter(upload => !isPhoto(upload));

  for (const upload of toDelete) {
    try {
      await deleteFile(upload.file);
    } catch {
      // Already gone, or never written. Either way there is nothing to do.
    }
  }

  if (toKeep.length > 0) {
    await storage.set(LEGACY_KEY, JSON.stringify(toKeep));
  } else {
    await storage.remove(LEGACY_KEY);
  }

  // Last, so a launch that dies partway through runs the sweep again rather
  // than leaving photo entries behind under a marker that says otherwise.
  await storage.set(CLEARED_KEY, true);
  console.log(`Cleared ${toDelete.length} legacy photo upload(s).`);
}
